import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { GlobalDashboardRepository } from '@app/domain/ports/global-dashboard.repository';
import {
  GlobalDashboardQuery,
  GlobalDashboardSummary,
} from '@app/domain/models/global-dashboard.model';
import { TpvDashboardRepository } from '@app/domain/ports/tpv/tpv-dashboard.repository';
import type { TpvDashboardQuery } from '@app/domain/models/tpv/tpv-dashboard-summary.model';
import {
  DashboardPeriodSelection,
  dashboardPeriodName,
} from '@app/domain/models/dashboard-period.model';
import { tpvDashboardDateRange } from '@app/domain/models/tpv/tpv-dashboard-period';
import { PaymentsDashboardRepository } from '@app/domain/ports/payments-dashboard.repository';
import type { PaymentsDashboardQuery } from '@app/domain/models/payments-dashboard/payments-dashboard.model';
import { GlobalDashboardSummaryDto } from '@app/data/dtos/global-dashboard.dto';
import {
  mapGlobalDashboardSummary,
  mergePaymentsIntoGlobalSummary,
  mergeTpvIntoGlobalSummary,
} from '@app/data/mappers/global-dashboard.mapper';
import { UserRoleService } from '@app/core/auth/user-role.service';
import { silentBackgroundReadContext } from '@app/core/http/silent-background-read.context';
import { REQUEST_ATTEMPT_TIMEOUT_MS } from '@app/core/http/request-timeout.context';
import {
  GlobalDashboardForbiddenError,
  GlobalDashboardUnavailableError,
} from '@app/domain/models/global-dashboard.errors';
import { environment } from 'src/environments/environment';

/** Mismo periodo (y, en `custom`, las mismas fechas) que "Todo". */
function toPaymentsQuery(query: GlobalDashboardQuery): PaymentsDashboardQuery {
  return query.period === 'custom'
    ? { period: 'custom', dateFrom: query.dateFrom, dateTo: query.dateTo }
    : { period: query.period };
}

/**
 * El periodo de "Todo" como la selección que usa el dashboard de TPV.
 *
 * Un `custom` sin sus dos fechas no lo produce la pantalla (`buildCustomPeriod` no lo
 * deja construir); si llegara, se trata como "mes", que es lo que la línea TPV pedía
 * antes en ese caso.
 */
function toPeriodSelection(query: GlobalDashboardQuery): DashboardPeriodSelection {
  if (query.period === 'custom') {
    return query.dateFrom && query.dateTo
      ? { kind: 'custom', from: query.dateFrom, to: query.dateTo }
      : { kind: 'mes' };
  }
  return { kind: query.period };
}

/**
 * La MISMA consulta que arma el dashboard de TPV: `period` exacto y las fechas de
 * `tpvDashboardDateRange` (días LOCALES; el fin a las 23:59:59). Sin `period` el back
 * corta en `now()` y descarta el fin de un rango específico (tpv_api `_rango_dashboard`).
 */
function toTpvQuery(query: GlobalDashboardQuery, ahora: Date): TpvDashboardQuery {
  const seleccion = toPeriodSelection(query);
  return { period: dashboardPeriodName(seleccion), ...tpvDashboardDateRange(seleccion, ahora) };
}

/**
 * Dashboard Global remoto: combina tres endpoints.
 *
 *  1. `GET /api/v1/dashboard/global-summary/` — SPEI + CARDS agregados al
 *     nivel más alto del usuario (el backend deriva el scope del JWT).
 *  2. `GET /api/v1/tpv/dashboard/` (repo TPV existente) — solo si el usuario
 *     tiene línea TPV. Se fusiona en el front porque el scope por comercios de
 *     TPV vive en tpv_api y duplicarlo en el endpoint global sería riesgo de
 *     fuga de datos.
 *  3. `GET /api/v1/payments/dashboard/` (port de la Pasarela de Pagos) — solo si
 *     el usuario tiene gestor PAYMENTS; mismo criterio que TPV.
 *
 * Si TPV o pagos fallan (o tardan más de 20 s), el dashboard degrada con gracia:
 * se muestra lo demás y esa línea se omite (mismo criterio que availableServices).
 *
 * Si `global-summary` falla, el fallo LLEGA a la pantalla (feature 108): antes se
 * tragaba con un resumen vacío y un 403 se veía como "dashboard sin datos".
 * 403 ⇒ `GlobalDashboardForbiddenError`, 404 ⇒ `GlobalDashboardUnavailableError`,
 * lo demás (red, 5xx, tiempo agotado) viaja tal cual y la pantalla ofrece
 * reintentar. Marcas por petición, puestas AQUÍ (el origen):
 *
 * - `SILENT_BACKGROUND_READ`: la pantalla pinta su propio mensaje por motivo, así
 *   que el modal genérico saldría DOS veces. No toca la sesión: un 401 vencido
 *   sigue abriendo su diálogo (la ruta no está en ningún `EXCLUDED_*`).
 * - `REQUEST_ATTEMPT_TIMEOUT_MS` = 20 s por intento, y NO un `timeout()` en este
 *   `pipe`: encima de la cadena vencería con el diálogo de sesión abierto y dejaría
 *   el refresh de toda la app colgado (CLAUDE.md §Known Security Debt).
 */
@Injectable()
export class GlobalDashboardRemoteDataSource extends GlobalDashboardRepository {
  private readonly http = inject(HttpClient);
  private readonly tpvDashboardRepository = inject(TpvDashboardRepository);
  private readonly userRole = inject(UserRoleService);
  private readonly paymentsDashboard = inject(PaymentsDashboardRepository);

  /** Mismo criterio que las llamadas de balances: 20s para no colgar la pantalla. */
  private readonly HTTP_TIMEOUT_MS = 20000;

  override getSummary(query: GlobalDashboardQuery): Observable<GlobalDashboardSummary> {
    const hasTpvLine = this.userRole.getBusinessLines().includes('tpv');
    // Misma regla que la línea "Pasarela de Pagos": gestor PAYMENTS y módulo encendido.
    const hasPayments = this.userRole.canUseEcommerce();

    const globalSummary$ = this.fetchGlobalSummary(query);
    if (!hasTpvLine && !hasPayments) {
      return globalSummary$;
    }

    const tpv$ = hasTpvLine ? this.fetchTpv(query) : of(null);
    // Sin gestor PAYMENTS ni siquiera se pide (R35).
    const payments$ = hasPayments
      ? this.paymentsDashboard.getDashboard(toPaymentsQuery(query)).pipe(
          timeout(this.HTTP_TIMEOUT_MS),
          // Degradación con gracia: si pagos falla, null ⇒ no se fusiona la línea (R34).
          catchError(() => of(null))
        )
      : of(null);

    return forkJoin([globalSummary$, tpv$, payments$]).pipe(
      map(([summary, tpv, pagos]) => {
        // TPV primero y pagos después; cada fusión re-alinea TODAS las series sobre un eje
        // común por bucket (`alinearSeriesAlEje`), así que ninguna queda en una posición ajena.
        const conTpv = tpv ? mergeTpvIntoGlobalSummary(summary, tpv) : summary;
        return pagos ? mergePaymentsIntoGlobalSummary(conTpv, pagos) : conTpv;
      })
    );
  }

  private fetchTpv(query: GlobalDashboardQuery) {
    return this.tpvDashboardRepository
      .getDashboard(toTpvQuery(query, new Date()))
      .pipe(
        timeout(this.HTTP_TIMEOUT_MS),
        // Degradación con gracia: si TPV falla, null ⇒ no se fusiona la línea.
        catchError(() => of(null))
      );
  }

  private fetchGlobalSummary(query: GlobalDashboardQuery): Observable<GlobalDashboardSummary> {
    let params = new HttpParams().set('period', query.period);
    if (query.period === 'custom' && query.dateFrom && query.dateTo) {
      params = params.set('date_from', query.dateFrom).set('date_to', query.dateTo);
    }

    return this.http
      .get<GlobalDashboardSummaryDto>(
        `${environment.apiUrl}/api/v1/dashboard/global-summary/`,
        {
          params,
          context: silentBackgroundReadContext().set(REQUEST_ATTEMPT_TIMEOUT_MS, this.HTTP_TIMEOUT_MS),
        }
      )
      .pipe(
        map(mapGlobalDashboardSummary),
        catchError((err: unknown) => throwError(() => typedFailure(err)))
      );
  }
}

/** 403 y 404 tienen copy propio y sin reintento; el resto se propaga tal cual. */
function typedFailure(err: unknown): unknown {
  if (err instanceof HttpErrorResponse) {
    if (err.status === 403) return new GlobalDashboardForbiddenError();
    if (err.status === 404) return new GlobalDashboardUnavailableError();
  }
  return err;
}
